import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";

/**
 * FilterBar — replaces multiple FilterMenu components.
 * Desktop: renders one dropdown button per filter group.
 * Mobile: renders a single "Filtres" button that opens a bottom drawer.
 *
 * Props:
 *   filters: Array<{ title: string, list: Array<{id?, name}>, onSelect: (values) => void }>
 */
const FilterBar = ({ filters = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selections, setSelections] = useState(
    Object.fromEntries(filters.map((f) => [f.title, []]))
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchors, setAnchors] = useState(
    Object.fromEntries(filters.map((f) => [f.title, null]))
  );

  const totalSelected = Object.values(selections).flat().length;

  const toggleItem = (filterTitle, value) => {
    setSelections((prev) => {
      const current = prev[filterTitle] || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      filters.find((f) => f.title === filterTitle)?.onSelect(updated);
      return { ...prev, [filterTitle]: updated };
    });
  };

  const resetAll = () => {
    const cleared = Object.fromEntries(filters.map((f) => [f.title, []]));
    setSelections(cleared);
    filters.forEach((f) => f.onSelect([]));
  };

  if (isMobile) {
    return (
      <>
        <Button
          size="small"
          variant={totalSelected > 0 ? "contained" : "outlined"}
          color="primary"
          startIcon={<FilterListIcon />}
          onClick={() => setDrawerOpen(true)}
          sx={{ flexShrink: 0, textTransform: "none" }}
        >
          Filtres{totalSelected > 0 ? ` (${totalSelected})` : ""}
        </Button>
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "16px 16px 0 0",
              maxHeight: "75vh",
              overflow: "auto",
            },
          }}
        >
          <Box sx={{ p: 2, pb: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography fontWeight={700} fontSize={18}>
                Filtres
                {totalSelected > 0 && (
                  <Typography
                    component="span"
                    fontSize={13}
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    ({totalSelected} sélectionné{totalSelected > 1 ? "s" : ""})
                  </Typography>
                )}
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            {totalSelected > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={resetAll}
                sx={{ mb: 2, p: 0, fontSize: 13, textTransform: "none" }}
              >
                Réinitialiser tout
              </Button>
            )}
            {filters.map((f) => (
              <Box key={f.title} mb={2}>
                <Typography
                  fontWeight={700}
                  fontSize={12}
                  color="#1A1D23"
                  mb={0.5}
                  sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  {f.title}
                </Typography>
                {f.list.map((item) => (
                  <Box
                    key={item.id ?? item.name}
                    onClick={() => toggleItem(f.title, item.name)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1,
                      borderBottom: "1px solid #F0F2F5",
                      cursor: "pointer",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                    }}
                  >
                    <Typography fontSize={14} color="#111111">
                      {item.name}
                    </Typography>
                    <Checkbox
                      checked={(selections[f.title] || []).includes(item.name)}
                      color="primary"
                      size="small"
                      readOnly
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Drawer>
      </>
    );
  }

  // Desktop: one dropdown button per filter group
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {filters.map((f) => {
        const selected = selections[f.title] || [];
        return (
          <Box key={f.title}>
            <Button
              sx={{ textTransform: "none", border: "1px solid transparent" }}
              variant={selected.length > 0 ? "contained" : "outlined"}
              color="primary"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={(e) =>
                setAnchors((prev) => ({ ...prev, [f.title]: e.currentTarget }))
              }
            >
              {f.title}
              {selected.length > 0 ? ` (${selected.length})` : ""}
            </Button>
            <Menu
              anchorEl={anchors[f.title]}
              open={Boolean(anchors[f.title])}
              onClose={() =>
                setAnchors((prev) => ({ ...prev, [f.title]: null }))
              }
              PaperProps={{
                sx: {
                  border: "1px solid #0001FC",
                  borderRadius: "8px",
                  minWidth: "240px",
                  overflow: "hidden",
                  mt: 0.5,
                },
              }}
            >
              {f.list.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => toggleItem(f.title, item.name)}
                  sx={{
                    "&:hover": {
                      background: "#E4EAFC",
                      ".MuiTypography-root, .MuiCheckbox-root": {
                        color: "#0001FC",
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={item.name}
                    sx={{
                      ".MuiTypography-root": {
                        flex: 2,
                        color: "#111111",
                        fontSize: "14px",
                      },
                    }}
                  />
                  <Checkbox
                    checked={selected.includes(item.name)}
                    size="small"
                  />
                </MenuItem>
              ))}
            </Menu>
          </Box>
        );
      })}
    </Box>
  );
};

export default FilterBar;
